import { useState, useEffect, useCallback } from 'react';
import { useUseCelo } from '../../../providers/UseCeloProvider';
import type {
  SubscriptionDisplay,
  UseSubscriptionReturn,
  PlanChangeRequest,
  CancelRequest,
  PauseRequest
} from '../../../types/subscription-manager.types';
import { SubscriptionError } from '../../../types/subscription-manager.types';

/**
 * Custom hook for subscription management
 * Handles fetching, updating, pausing, resuming, and canceling subscriptions
 */
export function useSubscription(): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<SubscriptionDisplay | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<SubscriptionError | null>(null);

  const {
    paymentClient,
    getSubscription: getBaseSubscription,
    cancelSubscription: baseCancelSubscription,
    pauseSubscription: basePauseSubscription,
    resumeSubscription: baseResumeSubscription,
  } = useUseCelo();


  // Fetch subscription data
  const fetchSubscription = useCallback(async (): Promise<void> => {
    if (!paymentClient) {
      setError(new SubscriptionError('Payment client not available', 'PAYMENT_CLIENT_MISSING', 400));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const subscriptionData = await getBaseSubscription?.();

      if (subscriptionData?.subscription) {
        // Transform to SubscriptionDisplay format
        const sub = subscriptionData.subscription;
        const displaySubscription: SubscriptionDisplay = {
          ...sub,
          cancelAtPeriodEnd: false, // Will be updated from actual data
          gateway: 'STRIPE', // Will be updated from actual data
          // Convert string dates to Date objects if they exist
          canceledAt: sub.canceledAt ? new Date(sub.canceledAt) : undefined,
          pausedUntil: sub.pausedUntil ? new Date(sub.pausedUntil) : undefined,
        };

        setSubscription(displaySubscription);
      } else {
        setSubscription(null);
      }
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to fetch subscription',
            'FETCH_ERROR',
            500
          );
      setError(subscriptionError);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [getBaseSubscription, paymentClient]);

  // Update subscription plan
  const updatePlan = useCallback(async (planId: string): Promise<void> => {
    if (!subscription) {
      throw new SubscriptionError('No active subscription', 'NO_SUBSCRIPTION', 400);
    }

    if (!paymentClient) {
      throw new SubscriptionError('Update subscription not available', 'UPDATE_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      const request: PlanChangeRequest = {
        newPlanId: planId,
        immediate: true,
        prorationBehavior: 'create_prorations',
      };

      // Use paymentClient for subscription updates
      await paymentClient.updateSubscription(request);

      // Refetch subscription to get updated data
      await fetchSubscription();
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to update plan',
            'UPDATE_ERROR',
            500
          );
      setError(subscriptionError);
      throw subscriptionError;
    }
  }, [subscription, paymentClient, fetchSubscription]);

  // Cancel subscription
  const cancel = useCallback(async (request?: CancelRequest): Promise<void> => {
    if (!subscription) {
      throw new SubscriptionError('No active subscription', 'NO_SUBSCRIPTION', 400);
    }

    if (!baseCancelSubscription) {
      throw new SubscriptionError('Cancel subscription not available', 'CANCEL_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      const cancelOptions = request ? {
        ...request,
        reason: request.reason ? String(request.reason) : undefined
      } : {};
      await baseCancelSubscription(cancelOptions);

      // Refetch subscription to get updated status
      await fetchSubscription();
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to cancel subscription',
            'CANCEL_ERROR',
            500
          );
      setError(subscriptionError);
      throw subscriptionError;
    }
  }, [subscription, baseCancelSubscription, fetchSubscription]);

  // Pause subscription
  const pause = useCallback(async (request: PauseRequest): Promise<void> => {
    if (!subscription) {
      throw new SubscriptionError('No active subscription', 'NO_SUBSCRIPTION', 400);
    }

    if (!basePauseSubscription) {
      throw new SubscriptionError('Pause subscription not available', 'PAUSE_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      await basePauseSubscription(request);

      // Refetch subscription to get updated status
      await fetchSubscription();
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to pause subscription',
            'PAUSE_ERROR',
            500
          );
      setError(subscriptionError);
      throw subscriptionError;
    }
  }, [subscription, basePauseSubscription, fetchSubscription]);

  // Resume subscription
  const resume = useCallback(async (): Promise<void> => {
    if (!subscription) {
      throw new SubscriptionError('No subscription found', 'NO_SUBSCRIPTION', 400);
    }

    if (!baseResumeSubscription) {
      throw new SubscriptionError('Resume subscription not available', 'RESUME_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      await baseResumeSubscription();

      // Refetch subscription to get updated status
      await fetchSubscription();
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to resume subscription',
            'RESUME_ERROR',
            500
          );
      setError(subscriptionError);
      throw subscriptionError;
    }
  }, [subscription, baseResumeSubscription, fetchSubscription]);

  // Refetch (alias for fetchSubscription)
  const refetch = useCallback(async (): Promise<void> => {
    await fetchSubscription();
  }, [fetchSubscription]);

  // Load subscription on mount
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  return {
    subscription,
    loading,
    error,
    refetch,
    updatePlan,
    cancel,
    pause,
    resume,
  };
}